/**
 * Campo de data com calendário próprio — spec 01, "Data — calendário
 * próprio".
 *
 * Não é `input[type=date]`: aquele delega a UI inteira ao navegador (Chrome,
 * Firefox e Safari desenham o calendário cada um do seu jeito, nenhum no
 * traço fino do sistema), e o handoff pede um calendário com a cara do
 * Lumma Store. O texto digitado (`dd/mm/aaaa`) e o grid de dias são os dois
 * jeitos de chegar na mesma data — a pessoa usa o que for mais rápido no
 * momento.
 *
 * O popover vai por `createPortal` para o `<body>`, com posição calculada a
 * partir do campo — mesmo motivo do menu de ações da tabela de clientes: um
 * popover `absolute` comum fica cortado pelo primeiro ancestral com scroll.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { dataBRparaISO, dataISOparaBR, mascararDataBR } from '../plataforma/mascaras'
import { Icone } from './Icone'
import { MensagemDeErro } from './Campos'

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

const DIAS_DA_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']

function paraISO(data: Date): string {
  const ano = data.getFullYear()
  const mes = String(data.getMonth() + 1).padStart(2, '0')
  const dia = String(data.getDate()).padStart(2, '0')
  return `${ano}-${mes}-${dia}`
}

/** As células do mês: `null` antes do dia 1 e depois do último dia. */
function celulasDoMes(ano: number, mes: number): (number | null)[] {
  const primeiroDiaSemana = new Date(ano, mes, 1).getDay()
  const diasNoMes = new Date(ano, mes + 1, 0).getDate()

  const celulas: (number | null)[] = Array(primeiroDiaSemana).fill(null)
  for (let dia = 1; dia <= diasNoMes; dia++) celulas.push(dia)
  return celulas
}

export function CampoData({
  rotulo,
  value,
  onChange,
  erro,
  dica,
}: {
  rotulo: string
  /** ISO `aaaa-mm-dd`, ou string vazia. */
  value: string
  onChange: (iso: string) => void
  erro?: string
  dica?: string
}) {
  const [texto, setTexto] = useState(() => dataISOparaBR(value))
  const [aberto, setAberto] = useState(false)
  const [posicao, setPosicao] = useState({ top: 0, left: 0, largura: 0 })
  const hoje = new Date()
  const referencia = value ? new Date(`${value}T00:00:00`) : hoje
  const [mesExibido, setMesExibido] = useState({ ano: referencia.getFullYear(), mes: referencia.getMonth() })
  const involucroRef = useRef<HTMLDivElement>(null)

  // O texto digitado só segue o `value` de fora quando a pessoa não está com
  // o popover aberto escolhendo um dia — senão a digitação em outro campo do
  // formulário que dispara um re-render apagaria o que ela acabou de digitar.
  useEffect(() => {
    if (!aberto) setTexto(dataISOparaBR(value))
  }, [value, aberto])

  const abrir = () => {
    const retangulo = involucroRef.current?.getBoundingClientRect()
    if (retangulo) {
      setPosicao({ top: retangulo.bottom + 6, left: retangulo.left, largura: retangulo.width })
    }
    const base = value ? new Date(`${value}T00:00:00`) : new Date()
    setMesExibido({ ano: base.getFullYear(), mes: base.getMonth() })
    setAberto(true)
  }

  useEffect(() => {
    if (!aberto) return
    const fechar = () => setAberto(false)
    window.addEventListener('scroll', fechar, true)
    window.addEventListener('resize', fechar)
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberto(false)
    }
    document.addEventListener('keydown', aoTeclar)
    return () => {
      window.removeEventListener('scroll', fechar, true)
      window.removeEventListener('resize', fechar)
      document.removeEventListener('keydown', aoTeclar)
    }
  }, [aberto])

  const aoDigitar = (bruto: string) => {
    const mascarado = mascararDataBR(bruto)
    setTexto(mascarado)
    if (mascarado === '') {
      onChange('')
      return
    }
    const iso = dataBRparaISO(mascarado)
    if (iso) onChange(iso)
  }

  const escolher = (dia: number) => {
    onChange(paraISO(new Date(mesExibido.ano, mesExibido.mes, dia)))
    setAberto(false)
  }

  const limpar = () => {
    onChange('')
    setAberto(false)
  }

  const irParaHoje = () => {
    onChange(paraISO(hoje))
    setAberto(false)
  }

  const trocarMes = (delta: number) => {
    setMesExibido((atual) => {
      const data = new Date(atual.ano, atual.mes + delta, 1)
      return { ano: data.getFullYear(), mes: data.getMonth() }
    })
  }

  const isoSelecionado = value || null
  const id = useId()
  const idErro = `${id}-erro`
  const idDica = `${id}-dica`

  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-[13px] font-semibold text-texto">
        {rotulo}
      </label>

      <div ref={involucroRef} className="composto" aria-invalid={erro ? true : undefined}>
        <input
          id={id}
          value={texto}
          onChange={(evento) => aoDigitar(evento.target.value)}
          onFocus={abrir}
          inputMode="numeric"
          placeholder="dd/mm/aaaa"
          aria-invalid={erro ? true : undefined}
          aria-describedby={erro ? idErro : dica ? idDica : undefined}
        />
        <button
          type="button"
          onClick={() => (aberto ? setAberto(false) : abrir())}
          aria-label={`Abrir calendário para ${rotulo}`}
          aria-expanded={aberto}
          className="flex shrink-0 items-center text-texto-suave"
        >
          <Icone nome="calendario" tamanho={18} />
        </button>
      </div>

      {erro ? (
        <MensagemDeErro id={idErro}>{erro}</MensagemDeErro>
      ) : (
        dica && (
          <p id={idDica} className="mt-1.5 text-xs text-texto-fraco">
            {dica}
          </p>
        )
      )}

      {aberto &&
        createPortal(
          <>
            <button
              type="button"
              aria-hidden
              tabIndex={-1}
              onClick={() => setAberto(false)}
              className="fixed inset-0 z-40 cursor-default"
            />
            <div
              role="dialog"
              aria-label="Calendário"
              style={{ top: posicao.top, left: posicao.left }}
              className="fixed z-50 w-56 rounded-sistema border border-borda bg-superficie p-3 shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="font-titulo text-[13px] font-semibold text-tinta">
                  {MESES[mesExibido.mes]} {mesExibido.ano}
                </span>
                <div className="flex gap-0.5">
                  <button
                    type="button"
                    onClick={() => trocarMes(-1)}
                    aria-label="Mês anterior"
                    className="flex h-6 w-6 items-center justify-center rounded-sistema text-texto-suave hover:bg-divisoria"
                  >
                    <Icone nome="chevron_esquerda" tamanho={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => trocarMes(1)}
                    aria-label="Próximo mês"
                    className="flex h-6 w-6 items-center justify-center rounded-sistema text-texto-suave hover:bg-divisoria"
                  >
                    <Icone nome="chevron_direita" tamanho={14} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-0.5">
                {DIAS_DA_SEMANA.map((letra, i) => (
                  <span
                    key={i}
                    className="flex h-6 items-center justify-center text-[10px] font-semibold text-texto-fraco"
                  >
                    {letra}
                  </span>
                ))}

                {celulasDoMes(mesExibido.ano, mesExibido.mes).map((dia, i) => {
                  if (dia === null) return <span key={i} />
                  const iso = paraISO(new Date(mesExibido.ano, mesExibido.mes, dia))
                  const selecionado = iso === isoSelecionado
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => escolher(dia)}
                      aria-pressed={selecionado}
                      className={`tabular flex h-7 w-7 items-center justify-center rounded-full text-[12px] ${
                        selecionado
                          ? 'bg-marca font-semibold text-white'
                          : 'text-tinta hover:bg-divisoria'
                      }`}
                    >
                      {dia}
                    </button>
                  )
                })}
              </div>

              <div className="mt-2 flex items-center justify-between border-t border-divisoria pt-2">
                <button
                  type="button"
                  onClick={limpar}
                  className="text-[12.5px] font-semibold text-texto-suave hover:text-texto"
                >
                  Limpar
                </button>
                <button
                  type="button"
                  onClick={irParaHoje}
                  className="text-[12.5px] font-semibold text-marca hover:text-marca-forte"
                >
                  Hoje
                </button>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  )
}
